<template>
  <div v-loading="loading" class="nx-page meterenergy-analysis">
    <el-card shadow="never">
      <div slot="header" class="card-head">
        <span>用电总览看板（抄表）</span>
        <div class="filters">
          <el-date-picker
            v-model="billingMonth"
            type="month"
            placeholder="账单月（默认最新）"
            value-format="yyyyMM"
            clearable
            size="small"
            style="width: 150px"
          />
          <el-button type="primary" size="small" @click="load">查询</el-button>
        </div>
      </div>
      <div class="kpi-row">
        <div class="kpi-card">
          <div class="kpi-label">账单月用电合计（kWh）</div>
          <div class="kpi-value">{{ fmtNum(data.totalKwh) }}</div>
          <div v-if="data.monthOnMonthPercent != null" class="kpi-sub">环比 {{ fmtPct(data.monthOnMonthPercent) }}</div>
        </div>
        <div class="kpi-card">
          <div class="kpi-label">抄表记录条数</div>
          <div class="kpi-value">{{ data.recordCount != null ? data.recordCount : '—' }}</div>
        </div>
        <div class="kpi-card">
          <div class="kpi-label">覆盖电类目数</div>
          <div class="kpi-value">{{ data.categoryCount != null ? data.categoryCount : '—' }}</div>
        </div>
        <div class="kpi-card">
          <div class="kpi-label">最近抄表日</div>
          <div class="kpi-value sm">{{ data.lastReadingDate || '—' }}</div>
        </div>
      </div>
      <el-row :gutter="16">
        <el-col :md="14" :xs="24">
          <div class="chart-title">近 6 个账单月用电（kWh）</div>
          <div ref="chartLine" class="chart-box" />
        </el-col>
        <el-col :md="10" :xs="24">
          <div class="chart-title">当前月类目占比</div>
          <div ref="chartPie" class="chart-box" />
        </el-col>
      </el-row>
      <div class="chart-title mt16">当前月类目用量（kWh）</div>
      <div ref="chartBar" class="chart-box" />
    </el-card>
  </div>
</template>

<script>
import * as echarts from 'echarts'
import { fetchElectricOverview } from './api'

export default {
  name: 'MeterEnergyElectricOverview',
  data() {
    return {
      loading: false,
      billingMonth: null,
      data: {},
      charts: []
    }
  },
  mounted() {
    this.load()
    window.addEventListener('resize', this.resize)
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.resize)
    this.disposeCharts()
  },
  methods: {
    fmtNum(v) {
      if (v == null || v === '') return '—'
      const n = Number(v)
      if (Number.isNaN(n)) return String(v)
      return n.toLocaleString('zh-CN', { maximumFractionDigits: 2 })
    },
    fmtPct(v) {
      if (v == null) return ''
      const n = Number(v)
      return (n >= 0 ? '+' : '') + n.toFixed(2) + '%'
    },
    disposeCharts() {
      this.charts.forEach((c) => c.dispose())
      this.charts = []
    },
    resize() {
      this.charts.forEach((c) => c.resize())
    },
    async load() {
      this.loading = true
      try {
        const res = await fetchElectricOverview({
          billingMonth: this.billingMonth || undefined
        })
        const pack = res.data && res.data.data
        this.data = pack || {}
        this.$nextTick(() => this.renderCharts())
      } catch (e) {
        this.$message.error((e && e.message) || '加载失败')
      } finally {
        this.loading = false
      }
    },
    renderCharts() {
      this.disposeCharts()
      const d = this.data
      const trend = d.monthlyTrend || []
      const line = echarts.init(this.$refs.chartLine)
      line.setOption({
        tooltip: { trigger: 'axis' },
        grid: { left: 48, right: 16, top: 24, bottom: 28 },
        xAxis: { type: 'category', data: trend.map((x) => x.billingMonth) },
        yAxis: { type: 'value', name: 'kWh' },
        series: [
          {
            type: 'line',
            smooth: true,
            data: trend.map((x) => Number(x.usageAmount) || 0),
            areaStyle: { color: 'rgba(234,88,12,0.15)' },
            lineStyle: { color: '#ea580c', width: 2 }
          }
        ]
      })
      this.charts.push(line)

      const cats = d.categoryUsages || []
      const pie = echarts.init(this.$refs.chartPie)
      pie.setOption({
        tooltip: { trigger: 'item' },
        series: [
          {
            type: 'pie',
            radius: ['40%', '65%'],
            data: cats.map((c) => ({ name: c.category, value: Number(c.usageAmount) || 0 })),
            label: { formatter: '{b}\n{d}%' }
          }
        ]
      })
      this.charts.push(pie)

      const sorted = [...cats].sort((a, b) => (Number(a.usageAmount) || 0) - (Number(b.usageAmount) || 0))
      const bar = echarts.init(this.$refs.chartBar)
      bar.setOption({
        tooltip: { trigger: 'axis' },
        grid: { left: 100, right: 24, top: 16, bottom: 28 },
        xAxis: { type: 'value', name: 'kWh' },
        yAxis: { type: 'category', data: sorted.map((c) => c.category) },
        series: [{ type: 'bar', data: sorted.map((c) => Number(c.usageAmount) || 0), itemStyle: { color: '#ea580c' } }]
      })
      this.charts.push(bar)
    }
  }
}
</script>

<style scoped>
.card-head {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
}
.filters {
  display: flex;
  align-items: center;
  gap: 8px;
}
.mb12 {
  margin-bottom: 12px;
}
.mt16 {
  margin-top: 16px;
}
.kpi-row {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 12px;
  margin-bottom: 16px;
}
@media (max-width: 1100px) {
  .kpi-row {
    grid-template-columns: repeat(2, 1fr);
  }
}
.kpi-card {
  background: #f8fafc;
  border-radius: 12px;
  padding: 14px 16px;
  border: 1px solid #e2e8f0;
}
.kpi-label {
  font-size: 12px;
  color: #64748b;
  margin-bottom: 6px;
}
.kpi-value {
  font-size: 22px;
  font-weight: 700;
  color: #0f172a;
}
.kpi-value.sm {
  font-size: 16px;
}
.kpi-sub {
  font-size: 12px;
  color: #059669;
  margin-top: 6px;
}
.chart-title {
  font-size: 14px;
  font-weight: 600;
  margin-bottom: 8px;
  color: #334155;
}
.chart-box {
  height: 300px;
  width: 100%;
}
</style>
